import React, { useEffect } from 'react';
import { FaInfo } from 'react-icons/fa';
import cn from 'classnames';

import { useVerifyTicketCommand } from 'legacy/events/hooks';
import { VerifyTicketResultDto } from 'legacy/events/types';

import Loader from 'legacy/common/components/layout/Loader';
import Button from 'legacy/common/components/button/Button';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { currencyFormat } from 'legacy/common/utils/currency';
import { fromNow } from 'legacy/common/utils/datetime';

type VerificationPopUpProps = {
  eventId: string;
  qrCodeString: string;
  onClose: () => void;
  onError: () => void;
  onSuccess: (data: VerifyTicketResultDto) => void;
};

const VerificationPopUp: React.FC<VerificationPopUpProps> = ({
  eventId,
  qrCodeString,
  onClose,
  onError,
  onSuccess
}) => {
  const verifyTicketCommand = useVerifyTicketCommand();

  useEffect(() => {
    verifyTicketCommand.mutate(
      {
        eventId,
        qrCodeString
      },
      {
        onSuccess,
        onError
      }
    );
  }, []);

  const { data, error } = verifyTicketCommand;

  return (
    <FadeIn>
      <div
        className="absolute left-0 bottom-0 w-full max-h-full bg-white rounded-t-3xl p-8 overflow-auto"
        data-testid="popup-qr-verification"
      >
        <Loader isLoading={verifyTicketCommand.isLoading}>
          <>
            {data && (
              <>
                <h1 className="flex items-center text-xl text-gray-500">
                  <div
                    className={cn(
                      'mr-2 w-10 h-10 rounded-xl flex items-center justify-center',
                      { 'bg-yellow-200': data.hasAlreadyScanned },
                      { 'bg-green-200': !data.hasAlreadyScanned }
                    )}
                  >
                    <div
                      className={cn(
                        'rounded-full border-1 border-orange-700 p-1',
                        { 'border-orange-700': data.hasAlreadyScanned },
                        { 'border-green-700': !data.hasAlreadyScanned }
                      )}
                    >
                      <FaInfo
                        size="10"
                        className={
                          data.hasAlreadyScanned
                            ? 'text-orange-700'
                            : 'text-green-700'
                        }
                      />
                    </div>
                  </div>
                  <span>
                    {data.hasAlreadyScanned
                      ? 'This ticket has already been scanned'
                      : 'QR Code Verified'}
                  </span>
                </h1>
                <div className="mt-6">
                  <div className="grid grid-cols-2 text-lg font-normal">
                    <div>Name:</div>
                    <div className="font-semibold">
                      {data.transaction.user.name}
                    </div>

                    <div>Amount Paid:</div>
                    <div className="font-semibold">
                      {`${currencyFormat(data.transaction.amount)} ${
                        data.transaction.currency
                      }`}
                    </div>

                    <div>Tier:</div>
                    <div className="font-semibold">
                      {data.transaction.tier.name}
                    </div>

                    <div>Unclaimed Tickets:</div>
                    <div className="font-semibold">
                      {data.numberOfUnclaimedTickets}
                    </div>

                    {data.scanDate && (
                      <>
                        <div>Last Scanned:</div>
                        <div className="font-semibold">
                          {fromNow(data.scanDate)}
                        </div>
                      </>
                    )}
                  </div>

                  {data?.tierRequestAnswers?.length > 0 && (
                    <div className="mt-10">
                      <p className="text-lg font-semibold">
                        Screening questions
                      </p>
                      {data?.tierRequestAnswers?.map((answer) => (
                        <div key={answer.questionId} className="mt-4">
                          <p className="text-base font-semibold">
                            {answer.question}
                          </p>
                          <p className="text-base font-normal">
                            {answer.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {data?.answers?.length > 0 && (
                    <div className="mt-10">
                      <p className="text-lg font-semibold">Guest questions</p>
                      {data?.answers?.map((answer) => (
                        <div key={answer.questionId} className="mt-4">
                          <p className="text-base font-semibold">
                            {answer.question}
                          </p>
                          <p className="text-base font-normal">
                            {answer.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="w-full my-10">
                  <Button
                    onClick={() => {
                      onClose();
                    }}
                  >
                    OK
                  </Button>
                </div>
              </>
            )}

            {error && (
              <>
                <h1 className="flex items-center text-xl text-gray-500">
                  <div className="mr-2 w-10 h-10 bg-red-200 rounded-xl flex items-center justify-center">
                    <div className="rounded-full border-1 border-red-700 p-1">
                      <FaInfo size="10" className="text-red-700" />
                    </div>
                  </div>
                  <span>QR Code Verification Error</span>
                </h1>

                <div className="mt-10">
                  <div className="text-xl text-red-500">{error.message}</div>
                  <div className="absolute bottom-0 w-full mb-10">
                    <Button
                      onClick={() => {
                        onClose();
                      }}
                    >
                      OK
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        </Loader>
      </div>
    </FadeIn>
  );
};

export default VerificationPopUp;
